import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { APP_VERSION, BUILD_GENERATED_AT, BUILD_ID } from '../buildInfo';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchPlatformBrandingCached, readCachedPlatformBranding } from '../utils/platformBrandingCache';
import './LoginPage.css';

const FALLBACK_LOGO = '/itraxx_logo_transparent.png';

function LoginPage({ onLoginSuccess }) {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [totp, setTotp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [platformLogo, setPlatformLogo] = useState(() => {
        const cached = readCachedPlatformBranding({ allowStale: true });
        return cached?.platformLogoUrl || FALLBACK_LOGO;
    });
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaMethod, setMfaMethod] = useState('');
    const [mfaChallengeId, setMfaChallengeId] = useState('');
    const [mfaSetupToken, setMfaSetupToken] = useState('');
    const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
    const [setupQrCode, setSetupQrCode] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [setupLoading, setSetupLoading] = useState(false);
    const totpInputRef = useRef(null);
    const currentYear = new Date().getFullYear();

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

    const getLocalizedAuthError = (err, fallbackKey) => {
        const raw = String(err?.response?.data?.error || '').trim().toLowerCase();
        if (raw.includes('invalid') && (raw.includes('credential') || raw.includes('password'))) {
            return t('auth.invalid_credentials');
        }
        return t(fallbackKey);
    };

    const resetMfaState = () => {
        setMfaRequired(false);
        setMfaMethod('');
        setMfaChallengeId('');
    };

    const completeLogin = (payload) => {
        onLoginSuccess(payload.user, payload.token);
    };

    const verifySmsCode = async () => {
        const normalizedCode = String(totp || '').replace(/\D/g, '').slice(0, 6);
        const response = await axios.post(`${API_URL}/auth/login-sms-verify`, {
            challengeId: mfaChallengeId,
            code: normalizedCode 
        }, {
            withCredentials: true
        });

        if (response.data?.success) {
            resetMfaState();
            setMfaSetupRequired(false);
            completeLogin(response.data);
            return true;
        }

        setError(t('login_page.verification_failed'));
        return false;
    };

    useEffect(() => {
        const trackPageView = async () => {
            try {
                const payload = {
                    path: window.location.pathname,
                    search: window.location.search || '',
                    referrer: document.referrer || '',
                    pageKey: 'dispatch-login',
                    lang: document.documentElement.lang || ''
                };
                if (navigator.sendBeacon) {
                    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                    navigator.sendBeacon(`${API_URL}/public/analytics/pageview`, blob);
                    return;
                }
                await axios.post(`${API_URL}/public/analytics/pageview`, payload, {
                    timeout: 1000
                });
            } catch (e) {
            }
        };

        const fetchPlatformLogo = async () => {
            try {
                const branding = await fetchPlatformBrandingCached({ timeout: 1500 });
                setPlatformLogo(branding?.platformLogoUrl || FALLBACK_LOGO);
            } catch (e) {
            }
        };

        let idleId = null;
        let timeoutId = null;

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(() => {
                trackPageView();
            }, { timeout: 1200 });
        } else {
            timeoutId = window.setTimeout(() => {
                trackPageView();
            }, 250);
        }

        fetchPlatformLogo();

        return () => {
            if (idleId !== null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleId);
            }
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    useEffect(() => {
        if (!(mfaRequired || mfaSetupRequired)) {
            return;
        }

        const targetInput = totpInputRef.current;
        if (!targetInput) {
            return;
        }

        window.requestAnimationFrame(() => {
            targetInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetInput.focus({ preventScroll: true });
            targetInput.select();
        });
    }, [mfaRequired, mfaMethod, mfaSetupRequired]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const resolvedEmail = String(email || '').trim();
            const resolvedPassword = String(password || '');

            if (mfaRequired && mfaMethod === 'sms' && mfaChallengeId) {
                await verifySmsCode();
                return;
            }

            const response = await axios.post(`${API_URL}/auth/login`, {
                email: resolvedEmail,
                password: resolvedPassword,
                totp: totp || undefined
            }, {
                withCredentials: true
            });

            if (response.data.success) {
                resetMfaState();
                setMfaSetupRequired(false);
                completeLogin(response.data);
                return;
            }

            if (response.data.mfaRequired) {
                setMfaRequired(true);
                setMfaSetupRequired(false);
                setMfaMethod(response.data.method || 'totp');
                setMfaChallengeId(response.data.challengeId || '');
                setError('');
                return;
            }

            if (response.data.mfaSetupRequired && response.data.method === 'totp' && response.data.setupToken) {
                resetMfaState();
                setMfaSetupRequired(true);
                setMfaSetupToken(response.data.setupToken);
                setError(t('login_page.mfa_setup_required'));
                return;
            }
        } catch (err) {
            console.error('[Login] Error:', err);
            setError(getLocalizedAuthError(err, 'login_page.login_failed'));
        } finally {
            setLoading(false);
        }
    };

    const startTotpSetup = async () => {
        if (!mfaSetupToken) return;
        setSetupLoading(true);
        setError('');
        try {
            const res = await axios.post(
                `${API_URL}/2fa/setup`,
                {},
                { headers: { Authorization: `Bearer ${mfaSetupToken}` } }
            );
            setSetupQrCode(res.data?.qrCode || '');
            setBackupCodes(Array.isArray(res.data?.backupCodes) ? res.data.backupCodes : []);
        } catch (err) {
            setError(t('login_page.mfa_setup_start_failed'));
        } finally {
            setSetupLoading(false);
        }
    };

    const verifyTotpSetup = async () => {
        if (!mfaSetupToken || !totp) return;
        setSetupLoading(true);
        setError('');
        try {
            const res = await axios.post(
                `${API_URL}/2fa/verify-and-enable`,
                { token: totp },
                { headers: { Authorization: `Bearer ${mfaSetupToken}` } }
            );
            if (res.data?.success && res.data?.token && res.data?.user) {
                onLoginSuccess(res.data.user, res.data.token);
                return;
            }
            setError(t('login_page.verification_failed'));
        } catch (err) {
            setError(getLocalizedAuthError(err, 'login_page.verification_failed'));
        } finally {
            setSetupLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-page-overlay" />
            <div className="login-page-ambient login-page-ambient-left" />
            <div className="login-page-ambient login-page-ambient-right" />
            <div className="login-card" role="dialog" aria-label={t('auth.login')}>
                <div className="login-card-header">
                    {window.location.hostname.includes('staging') && (
                        <div className="staging-badge">{t('login_page.staging_badge')}</div>
                    )}
                    <div className="login-card-logo-frame">
                        <img
                            src={platformLogo || FALLBACK_LOGO}
                            alt=""
                            className="login-card-logo"
                            onError={() => {
                                if (platformLogo !== FALLBACK_LOGO) {
                                    setPlatformLogo(FALLBACK_LOGO);
                                    return;
                                }
                                setPlatformLogo(null);
                            }}
                            width={96}
                            height={96}
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                    <h1 className="login-card-title">{t('login_page.brand_title')}</h1>
                </div>

                <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
                    {error && (
                        <div className="login-error">
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">{t('common.email')}</label>
                        <div className="login-input-wrap">
                            <span className="login-input-icon" aria-hidden="true">
                                <i className="fas fa-user" />
                            </span>
                            <input
                                type="email"
                                id="email"
                                name="itraxx_email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="off"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">{t('common.password')}</label>
                        <div className="login-input-wrap">
                            <span className="login-input-icon" aria-hidden="true">
                                <i className="fas fa-lock" />
                            </span>
                            <input
                                type="password"
                                id="password"
                                name="itraxx_password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    {(mfaRequired || mfaSetupRequired) && (
                        <div className="form-group">
                            <label htmlFor="totp">{t('login_page.one_time_code')}</label>
                            <div className="login-input-wrap login-input-wrap-code">
                                <input
                                    type="text"
                                    id="totp"
                                    ref={totpInputRef}
                                    className="login-code-input"
                                    value={totp}
                                    onChange={(e) => setTotp(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))} 
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoComplete="one-time-code"
                                    disabled={loading || setupLoading}
                                />
                            </div>
                        </div>
                    )}

                    {mfaSetupRequired && (
                        <div className="form-group">
                            {!setupQrCode ? (
                                <button
                                    type="button"
                                    className="login-button"
                                    onClick={startTotpSetup}
                                    disabled={loading || setupLoading}
                                >
                                    {setupLoading ? t('login_page.preparing') : t('login_page.start_2fa_setup')}
                                </button>
                            ) : (
                                <div className="login-mfa-setup">
                                    <div className="login-qr-wrap">
                                        <img src={setupQrCode} alt={t('login_page.qr_alt')} className="login-qr-code" />
                                    </div>
                                    {backupCodes.length > 0 && (
                                        <div className="login-backup-codes">
                                            <div className="login-backup-codes-title">{t('login_page.backup_codes')}</div>
                                            <div className="login-backup-codes-grid">
                                                {backupCodes.map((c) => (
                                                    <div key={c} className="login-backup-code-item">{c}</div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="login-button"
                                        onClick={verifyTotpSetup}
                                        disabled={loading || setupLoading || !totp}
                                    >
                                        {setupLoading ? t('login_page.verifying') : t('login_page.verify_and_login')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="login-button"
                        disabled={loading || setupLoading || mfaSetupRequired}
                    >
                        {loading ? t('login_page.logging_in') : t('auth.login')}
                    </button>
                </form>

                <div className="login-card-footer" title={t('login_page.generated_tooltip', { generatedAt: BUILD_GENERATED_AT })}>
                    <span>{t('login_page.footer_company', { year: currentYear })}</span>
                    <span aria-hidden="true">•</span>
                    <span>{t('login_page.version_label')} {`v${APP_VERSION}`}</span>
                    <span aria-hidden="true">•</span>
                    <span>{t('login_page.build_label')} {BUILD_ID}</span>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
